import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs';
import { Memoize } from 'typescript-memoize';

import {
  ReportsDashboardTemplatesActions,
  ReportsDashboardTemplatesSelectors,
} from '@core/reports-data';
import {
  ReportDashboardTemplate,
  ReportDashboardTemplateComponent,
  REPORTS_DEFAULT_VISIBILITY,
} from '@core/reports-types';

import { ReportComponentsDataFacade } from './components-data.facade';
import { ReportComponentsVisualizationsFacade } from './components-visualizations.facade';
import { ReportDashboardsFacade } from './dashboards.facade';

@Injectable()
export class ReportDashboardTemplatesLibraryFacade {
  private readonly store = inject(Store);
  private readonly componentsDataFacade = inject(ReportComponentsDataFacade);
  private readonly visualizationsFacade = inject(ReportComponentsVisualizationsFacade);
  private readonly dashboardsFacade = inject(ReportDashboardsFacade);

  public dashboardTemplates$: Observable<ReportDashboardTemplate[]> = this.store
    .select(ReportsDashboardTemplatesSelectors.meta)
    .pipe(
      filter(x => x.loaded),
      switchMap(() => this.store.select(ReportsDashboardTemplatesSelectors.entities)),
      shareReplay(1),
    );

  public templatesLoaded$: Observable<boolean> = this.store
    .select(ReportsDashboardTemplatesSelectors.meta)
    .pipe(
      map(x => x.loaded),
      shareReplay(1),
    );

  // set of component ids for loaded visualizations during this session
  private fetchedVisualizations = new Set<string>();

  public loadTemplatesOnce(): void {
    this.store
      .select(ReportsDashboardTemplatesSelectors.meta)
      .pipe(take(1))
      .subscribe(meta => {
        if (!meta.loaded && !meta.pending) {
          this.store.dispatch(ReportsDashboardTemplatesActions.fetchCollection());
        }
      });
  }

  public componentsForDashboardTemplate(
    templateId: string,
  ): Observable<ReportDashboardTemplateComponent[]> {
    return this.store
      .select(ReportsDashboardTemplatesSelectors.dashboardTemplateWithReferences(templateId))
      .pipe(map(template => template.components));
  }

  @Memoize(c => c.libraryId)
  public getComponentVisualization(component: ReportDashboardTemplateComponent) {
    return this.visualizationsFacade.getComponentVisualization({ component });
  }

  @Memoize(c => c.libraryId)
  public getComponentDataMeta(component: ReportDashboardTemplateComponent): Observable<{
    isLoaded: boolean;
    isPending: boolean;
    isFailed: boolean;
    error: any;
  }> {
    return this.componentsDataFacade.getComponentRawDataMeta({ component });
  }

  public loadMissingVisualizationData(component: ReportDashboardTemplateComponent) {
    if (this.fetchedVisualizations.has(component.localId!) === false) {
      this.visualizationsFacade.loadComponentVisualizationDataOnce({ component });
      this.fetchedVisualizations.add(component.localId!);
    }
  }

  public addDashboardFromTemplate(
    template: ReportDashboardTemplate & { components: ReportDashboardTemplateComponent[] },
  ): void {
    const { id, ...dashboard } = template;
    this.dashboardsFacade.duplicateDashboardInternal(
      {
        ...dashboard,
        visibility: REPORTS_DEFAULT_VISIBILITY,
        dateFilter: {
          dateRange: null,
          dateStart: null,
          dateEnd: null,
        },
        origin: 'template',
        templateId: template.id,
      },
      true,
    );
  }
}
